import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import { looseUuid } from "@thalamos/common";

/**
 * What kind of thing an action affected.
 *
 * Closed, like the entry kinds, keyed by its values, and stored as written — so
 * a member is added, never renamed.
 */
export const AuditTrailResourceKind = {
  user: "user",
  organisation: "organisation",
  membership: "membership",
  /** A young person's record — the subject of an assessment, not an account. */
  person: "person",
  /**
   * One merge of two records. Named so an entry can point at the row holding
   * the reason without the trail carrying the reason itself — see
   * `2026-09-10-the-trail-records-the-act-and-the-record-holds-the-reason`.
   */
  person_merge: "person_merge",
  /**
   * One assessment of a young person. Named so the trail can be searched for
   * everything done to an assessment without naming the child it is about.
   */
  assessment: "assessment",
  /**
   * A level of the configuration chain — a programme, a cooperative, a cohort.
   * Named so the trail can be searched for everything done to one body's
   * configuration, whether the change was made at the body or above it.
   */
  configuration_scope: "configuration_scope",
  /**
   * One recorded involvement between a young person and a party. Named so an
   * entry points at the row that says who was involved and under what, rather
   * than the trail restating it.
   */
  person_involvement: "person_involvement",
  /**
   * One ending of an involvement. Named so an entry can point at the row
   * holding the reason without the trail carrying the reason itself — see
   * `2026-09-10-the-trail-records-the-act-and-the-record-holds-the-reason`.
   */
  person_involvement_ending: "person_involvement_ending",
  /**
   * One guest access link, the means by which a guest reaches their
   * involvement. Named so every step of signing in through it can be found
   * together.
   */
  guest_access_link: "guest_access_link",
} as const;

export type AuditTrailResourceKind =
  (typeof AuditTrailResourceKind)[keyof typeof AuditTrailResourceKind];

export const AuditTrailResourceKindSchema = z.enum(
  AuditTrailResourceKind,
) satisfies ZodType<AuditTrailResourceKind & SnakeCase<AuditTrailResourceKind>>;

export type AuditTrailResourceUnbranded = {
  resourceKind: AuditTrailResourceKind;
  resourceId: string;
  resourceName?: string | null;
};

export const AuditTrailResourceSchema = z.object({
  resourceKind: AuditTrailResourceKindSchema,
  resourceId: looseUuid(),
  // Nullish rather than optional: most resources have no name to record, and
  // it comes back from a nullable column as an explicit null.
  resourceName: z.string().max(255).nullish(),
}) satisfies ZodType<AuditTrailResourceUnbranded>;

/**
 * One thing an action affected. An entry may name several: a membership being
 * granted touches a user, an organisation and the membership itself, and the
 * trail is searched by any of them.
 *
 * `resourceId` is deliberately unbranded: the ids here are of different kinds, so
 * any single brand would be wrong for most of them and a union of brands would
 * oblige every writer to say which.
 */
export type AuditTrailResource = z.infer<typeof AuditTrailResourceSchema>;
